// src/lib/lazyWithRetry.ts
//
// React.lazy wrapper that survives stale-chunk errors after a deploy.
//
// Why this exists: Vite emits content-hashed chunk filenames (e.g.
// ReservationsView-DYElElC0.js). When a user keeps the app open across a
// deploy, their cached index.html still references the OLD hashes. When they
// then navigate to a lazy-loaded view, the browser requests a chunk that no
// longer exists, the CDN serves the SPA-fallback HTML instead, and the import
// rejects with "Failed to fetch dynamically imported module" (Sentry issues
// ADAPTHUB-P/Q/R/S/T, 2026-09).
//
// Strategy (two layers):
//  1. Retry the in-flight import a few times with backoff — covers transient
//     network blips without any page disruption.
//  2. If the error is a chunk-load failure, delegate to the shared
//     recoverFromChunkError() (src/lib/chunkErrorRecovery.ts) which performs
//     ONE rate-limited page reload so the browser fetches a fresh index.html
//     with the current chunk hashes. The error-boundary fallback in
//     AuthedApp.tsx remains as the reactive backstop for anything that still
//     slips through.
//
// NOTE: detection + reload logic intentionally lives in chunkErrorRecovery.ts
// (single source of truth, single sessionStorage guard). This module only
// adds the proactive import-retry layer on top.

import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";
import { isChunkLoadError, recoverFromChunkError } from "./chunkErrorRecovery";

/** Backoff delays (ms) for in-flight import retries before falling back to a reload. */
const RETRY_DELAYS_MS = [200, 500, 1000];

/**
 * Drop-in replacement for React.lazy that retries failed dynamic imports.
 *
 * @example
 * const ReservationsView = lazyWithRetry(() => import("./views/ReservationsView"));
 */
// `any` (not `unknown`) is deliberate: under strictFunctionTypes,
// FC<SpecificProps> is NOT assignable to ComponentType<unknown> (props are
// contravariant), which broke every lazyWithRetry() call site. `any` is
// bivariant and accepts all view components; the returned
// LazyExoticComponent<T["default"]> still preserves each view's exact props.
export function lazyWithRetry<T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>,
): LazyExoticComponent<T["default"]> {
  return lazy(async () => {
    // Layer 1: quick in-flight retries for transient network failures.
    for (const delay of RETRY_DELAYS_MS) {
      try {
        return await factory();
      } catch (error) {
        if (!isChunkLoadError(error)) {
          throw error; // Application error — surface it immediately.
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // Layer 2: last attempt; on a chunk-load failure, hand off to the shared
    // rate-limited recovery (one reload) so the new deploy's asset hashes
    // are picked up. If the guard blocks the reload, rethrow and let the
    // error-boundary fallback handle it.
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error)) {
        recoverFromChunkError();
      }
      throw error;
    }
  });
}
